/**
 * Picks between the real API client (src/lib/api.ts) and the in-memory mock
 * (src/mocks/mock.ts) — same function names either way, so every page just
 * imports from here and never has to know which one is live.
 *
 * `VITE_USE_MOCK` is a three-way switch (build-time):
 *   - "1" forces mock mode, always.
 *   - "0" forces real mode, always — even if the probe below fails to
 *     answer. This is the production/staging setting (see .env.production
 *     / .env.staging): a real deployment must show the real network error
 *     instead of silently sliding into "mode démo" just because the API
 *     had a bad two seconds.
 *   - unset (local dev default) auto-detects: mock mode until a probe
 *     `GET /api/chouette/catalog` answers within 2s (`AbortSignal.timeout`),
 *     at which point `initDataSource()` flips the module-level flag —
 *     *and* falls back to mock again if that probe fails, which is what
 *     makes `npm run dev` work with zero backend out of the box.
 * `isDemoMode()` lets the shell render the small "mode démo" badge from the
 * spec — it never renders once VITE_USE_MOCK=0 has forced real mode.
 */
import * as real from "./api";
import * as mock from "../mocks/mock";
import type {
  AiJob,
  ApiCallResult,
  AppearanceInput,
  Catalog,
  Child,
  ChildCreateInput,
  ChildInput,
  ChildSheetsOut,
  ChouetteEventKind,
  CreateStoryBody,
  CreateStoryResponse,
  ListenEventKind,
  Me,
  PairingConsumeResponse,
  PairingCreateResponse,
  StoryCard,
  StoryDetail,
} from "./types";

const rawUseMock = import.meta.env.VITE_USE_MOCK;
const forcedMock = rawUseMock === "1";
const forcedReal = rawUseMock === "0";
let useMock = !forcedReal;
let probed = false;

export function isDemoMode(): boolean {
  return useMock;
}

/** Runs once at startup (see main.tsx). Resolves once the probe settles,
 *  never rejects — a network error just means "stay in mock mode" (unless
 *  forced real, see the header comment). */
export async function initDataSource(): Promise<void> {
  if (probed) return;
  probed = true;
  if (forcedMock) {
    useMock = true;
    return;
  }
  if (forcedReal) {
    useMock = false;
    return;
  }
  try {
    const res = await fetch(`${real.API_BASE}/chouette/catalog`, {
      signal: AbortSignal.timeout(2000),
    });
    useMock = !res.ok;
  } catch {
    useMock = true;
  }
}

export function getCatalog(childId?: string, opts: { adminView?: boolean } = {}): Promise<Catalog> {
  return useMock ? mock.mockGetCatalog(childId) : real.getCatalog(childId, opts);
}

export function getStory(id: string): Promise<StoryDetail> {
  return useMock ? mock.mockGetStory(id) : real.getStory(id);
}

export function getMyStories(childId?: string): Promise<StoryCard[]> {
  return useMock ? mock.mockGetMyStories(childId) : real.getMyStories(childId);
}

export function createStory(body: CreateStoryBody): Promise<CreateStoryResponse> {
  return useMock ? mock.mockCreateStory(body) : real.createStory(body);
}

export function setFavorite(storyId: string, favorite: boolean): Promise<void> {
  return useMock ? mock.mockSetFavorite(storyId, favorite) : real.setFavorite(storyId, favorite);
}

export function deleteStory(storyId: string): Promise<void> {
  return useMock ? mock.mockDeleteStory(storyId) : real.deleteStory(storyId);
}

export function listChildren(): Promise<Child[]> {
  return useMock ? mock.mockListChildren() : real.listChildren();
}

export function createChild(input: ChildCreateInput): Promise<Child> {
  return useMock ? mock.mockCreateChild(input) : real.createChild(input);
}

export function patchChild(id: string, input: Partial<ChildInput>): Promise<Child> {
  return useMock ? mock.mockPatchChild(id, input) : real.patchChild(id, input);
}

export function deleteChild(id: string): Promise<void> {
  return useMock ? mock.mockDeleteChild(id) : real.deleteChild(id);
}

export function putChildAppearance(childId: string, body: AppearanceInput): Promise<Child> {
  return useMock ? mock.mockPutChildAppearance(childId, body) : real.putChildAppearance(childId, body);
}

export function startChildSheets(childId: string): Promise<ChildSheetsOut> {
  return useMock ? mock.mockStartChildSheets(childId) : real.startChildSheets(childId);
}

export function getChildSheets(childId: string): Promise<ChildSheetsOut> {
  return useMock ? mock.mockGetChildSheets(childId) : real.getChildSheets(childId);
}

export function chooseChildSheet(childId: string, sheetId: string): Promise<Child> {
  return useMock ? mock.mockChooseChildSheet(childId, sheetId) : real.chooseChildSheet(childId, sheetId);
}

export function getChildSheetImageBlob(childId: string, sheetId: string): Promise<Blob> {
  return useMock
    ? mock.mockGetChildSheetImageBlob(childId, sheetId)
    : real.fetchChildSheetImageBlob(childId, sheetId);
}

export function getChildSheetPortraitBlob(childId: string, sheetId: string): Promise<Blob> {
  return useMock
    ? mock.mockGetChildSheetPortraitBlob(childId, sheetId)
    : real.fetchChildSheetPortraitBlob(childId, sheetId);
}

export function getMe(): Promise<Me> {
  return useMock ? mock.mockGetMe() : real.getMe();
}

export function setKidPin(pin: string): Promise<void> {
  return useMock ? mock.mockSetKidPin(pin) : real.setKidPin(pin);
}

export function verifyKidPin(pin: string): Promise<{ ok: boolean }> {
  return useMock ? mock.mockVerifyKidPin(pin) : real.verifyKidPin(pin);
}

export function deleteAccount(confirmEmail: string): Promise<{ deleted: boolean }> {
  return useMock ? mock.mockDeleteAccount(confirmEmail) : real.deleteAccount(confirmEmail);
}

export function requestPinReset(): Promise<void> {
  return useMock ? mock.mockRequestPinReset() : real.requestPinReset();
}

export function resetPin(token: string): Promise<void> {
  return useMock ? mock.mockResetPin(token) : real.resetPin(token);
}

export function unlockPack(id: string): Promise<void> {
  return useMock ? mock.mockUnlockPack(id) : real.unlockPack(id);
}

export function getAiJob(jobId: string): Promise<AiJob> {
  return useMock ? mock.mockGetAiJob(jobId) : real.getAiJob(jobId);
}

export function pairingCreate(targetUserId: string): Promise<PairingCreateResponse> {
  return useMock ? mock.mockPairingCreate(targetUserId) : real.pairingCreate(targetUserId);
}

export function pairingConsume(code: string, deviceLabel?: string): Promise<PairingConsumeResponse> {
  return useMock ? mock.mockPairingConsume(code, deviceLabel) : real.pairingConsume(code, deviceLabel);
}

export function postListen(storyId: string, event: ListenEventKind, at: string): Promise<ApiCallResult> {
  return useMock ? mock.mockPostListen(storyId, event, at) : real.postListen(storyId, event, at);
}

export function postEvent(kind: ChouetteEventKind, meta: Record<string, unknown>): Promise<ApiCallResult> {
  return useMock ? mock.mockPostEvent(kind, meta) : real.postEvent(kind, meta);
}

export { requestMagicLink, devLoginUrl, InsufficientCreditsError, currentUserId } from "./api";
